import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import VirtualGifts from './VirtualGifts';

const EnhancedChat = ({ user, matchedUser, onBack }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [showGifts, setShowGifts] = useState(false);
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);
  const [userPremiumStatus, setUserPremiumStatus] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [userCoins, setUserCoins] = useState(0);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);

  // Check if user has premium subscription
  useEffect(() => {
    const checkPremiumStatus = async () => {
      try {
        const { data: subscription, error } = await supabase
          .from('user_subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .single();

        // Determine if user is premium based on multiple criteria
        const isUserPremium = subscription && (
          // New way: check is_premium field directly
          subscription.is_premium === true ||
          // Old way: check status and expiry for backwards compatibility
          (subscription.status === 'active' && 
           new Date(subscription.expires_at) > new Date())
        );

        setUserPremiumStatus(isUserPremium);
        
        // Also fetch user coins
        const { data: coins } = await supabase
          .from('user_coins')
          .select('balance')
          .eq('user_id', user.id)
          .single();
          
        setUserCoins(coins?.balance || 0);
      } catch (error) {
        console.error('Error checking premium status:', error);
        setUserPremiumStatus(false);
        setUserCoins(0);
      }
    };

    checkPremiumStatus();
  }, [user.id]);

  // Function to load messages including gifts and photos
  const loadMessages = async () => {
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .or(`and(sender_id.eq.${user.id},receiver_id.eq.${matchedUser.id}),and(sender_id.eq.${matchedUser.id},receiver_id.eq.${user.id})`)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error loading messages:', error);
        return;
      }

      setMessages(data || []);
      scrollToBottom();
      
      // Mark messages as read after loading
      await markMessagesAsRead();
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  // Function to mark messages as read
  const markMessagesAsRead = async () => {
    try {
      // Get unread messages from this sender
      const { data: unreadMessages } = await supabase
        .from('messages')
        .select('id')
        .eq('sender_id', matchedUser.id)
        .eq('receiver_id', user.id);

      if (unreadMessages && unreadMessages.length > 0) {
        // Mark each message as read
        for (const message of unreadMessages) {
          await supabase
            .from('message_reads')
            .upsert({
              user_id: user.id,
              message_id: message.id,
              read_at: new Date().toISOString()
            }, {
              onConflict: 'user_id,message_id'
            });
        }
        console.log(`Marked ${unreadMessages.length} messages as read`);
      }
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  // Load messages when component starts
  useEffect(() => {
    loadMessages();
    
    const interval = setInterval(() => {
      loadMessages();
    }, 3000);

    return () => clearInterval(interval);
  }, [user.id, matchedUser.id]);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  // Function to send a regular text message
  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      const { error } = await supabase
        .from('messages')
        .insert([
          {
            sender_id: user.id,
            receiver_id: matchedUser.id,
            content: newMessage.trim(),
            message_type: 'text'
          }
        ]);

      if (error) {
        console.error('Error sending message:', error);
        return;
      }

      setNewMessage('');
      await loadMessages();
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  // Function to send a virtual gift
  const sendGift = async (gift) => {
    if (!userPremiumStatus) {
      alert('Virtual gifts are exclusive to Premium members! Upgrade to send gifts.');
      return;
    }

    // Check if user has enough coins BEFORE sending
    if (userCoins < gift.price) {
      alert(`You need ${gift.price} coins to send ${gift.name}! You currently have ${userCoins} coins.`);
      return;
    }

    try {
      // First deduct coins to ensure user has enough
      const { data: deductResult, error: coinsError } = await supabase
        .rpc('deduct_coins', {
          user_uuid: user.id,
          amount: gift.price
        });

      if (coinsError || !deductResult) {
        console.error('Error deducting coins:', coinsError);
        alert('Insufficient coins to send this gift!');
        return;
      }

      const { data: messageData, error } = await supabase
        .from('messages')
        .insert([
          {
            sender_id: user.id,
            receiver_id: matchedUser.id,
            content: `Sent a ${gift.name}`,
            message_type: 'gift',
            gift_data: JSON.stringify(gift)
          }
        ])
        .select('id')
        .single();

      if (error) {
        console.error('Error sending gift:', error);
        // If message fails, we should refund the coins but for now just alert
        alert('Failed to send gift message');
        return;
      }

      // Record the gift transaction
      if (messageData) {
        const { error: transactionError } = await supabase
          .from('gift_transactions')
          .insert([
            {
              sender_id: user.id,
              receiver_id: matchedUser.id,
              message_id: messageData.id,
              gift_id: gift.id,
              gift_name: gift.name,
              coins_spent: gift.price
            }
          ]);

        if (transactionError) {
          console.error('Error recording gift transaction:', transactionError);
        }

        // Add earned coins to receiver (they earned the gift!)
        const { error: earnError } = await supabase
          .rpc('add_earned_coins', {
            target_user_id: matchedUser.id,
            amount: gift.price,
            source: 'gift_received'
          });

        if (earnError) {
          console.error('Error adding earned coins to receiver:', earnError);
        }
      }

      // Update sender's local coin count
      setUserCoins(userCoins - gift.price);

      setShowGifts(false);
      await loadMessages();
      
      // Show success message with remaining coins
      alert(`${gift.name} sent successfully! ${gift.price} coins deducted. ${userCoins - gift.price} coins remaining. 🎉`);
    } catch (error) {
      console.error('Error sending gift:', error);
      alert('Failed to send gift. Please try again.');
    }
  };

  // Function to handle photo upload
  const handlePhotoUpload = async (event) => {
    if (!userPremiumStatus) {
      alert('Photo sharing is exclusive to Premium members! Upgrade to send photos.');
      return;
    }

    // Check if user has enough coins
    if (userCoins < 1) {
      alert('You need at least 1 coin to send a photo! Both sender and receiver pay 1 coin each. Get more coins by receiving gifts or upgrading your plan.');
      return;
    }

    const file = event.target.files[0];
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    setUploading(true);

    try {
      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}_${matchedUser.id}_${Date.now()}.${fileExt}`;
      const filePath = `chat-photos/${fileName}`;

      // Upload to Supabase storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('chat-images')
        .upload(filePath, file);

      if (uploadError) {
        console.error('Error uploading photo:', uploadError);
        alert('Failed to upload photo');
        return;
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('chat-images')
        .getPublicUrl(filePath);

      // Send photo message with coin deduction using our new function
      const { data: result, error: sendError } = await supabase
        .rpc('send_photo_message', {
          receiver_user_id: matchedUser.id,
          photo_url_param: urlData.publicUrl
        });

      if (sendError) {
        console.error('Error sending photo:', sendError);
        alert('Failed to send photo: ' + sendError.message);
        return;
      }

      const response = result[0];
      if (!response.success) {
        alert(response.message);
        return;
      }

      // Update local coin count
      setUserCoins(response.coins_remaining);
      
      // Show success message
      alert(`Photo sent successfully! 1 coin deducted from both you and the recipient. ${response.coins_remaining} coins remaining.`);
      
      setShowPhotoUpload(false);
      await loadMessages();
    } catch (error) {
      console.error('Error handling photo upload:', error);
      alert('Failed to send photo');
    } finally {
      setUploading(false);
    }
  };

  // Function to render different message types
  const renderMessage = (message) => {
    const isOwnMessage = message.sender_id === user.id;
    
    switch (message.message_type) {
      case 'gift':
        const giftData = message.gift_data ? JSON.parse(message.gift_data) : null;
        return (
          <div key={message.id} style={{
            display: 'flex',
            justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
            marginBottom: '15px'
          }}>
            <div style={{
              background: isOwnMessage 
                ? 'linear-gradient(135deg, #FF6B6B, #8A2BE2)' 
                : 'rgba(255, 255, 255, 0.15)',
              borderRadius: '20px',
              padding: '15px 20px',
              maxWidth: '70%',
              textAlign: 'center',
              border: '2px solid rgba(255, 215, 0, 0.5)',
              boxShadow: '0 8px 25px rgba(255, 215, 0, 0.3)'
            }}>
              {giftData && (
                <div>
                  <div style={{ fontSize: '3rem', marginBottom: '8px' }}>
                    {giftData.emoji}
                  </div>
                  <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                    {giftData.name}
                  </div>
                  <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>
                    💎 {giftData.price} coins
                  </div>
                </div>
              )}
              <div style={{ fontSize: '0.7rem', opacity: 0.7, marginTop: '8px' }}>
                {new Date(message.created_at).toLocaleTimeString()}
              </div>
            </div>
          </div>
        );

      case 'photo':
        return (
          <div key={message.id} style={{
            display: 'flex',
            justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
            marginBottom: '15px'
          }}>
            <div style={{
              background: isOwnMessage 
                ? 'linear-gradient(135deg, #00E9FF, #8A2BE2)' 
                : 'rgba(255, 255, 255, 0.15)',
              borderRadius: '20px',
              padding: '10px',
              maxWidth: '70%'
            }}>
              <img 
                src={message.photo_url} 
                alt="Shared photo" 
                style={{
                  maxWidth: '100%',
                  maxHeight: '200px',
                  borderRadius: '12px',
                  cursor: 'pointer'
                }}
                onClick={() => window.open(message.photo_url, '_blank')}
              />
              <div style={{ fontSize: '0.7rem', opacity: 0.7, marginTop: '5px' }}>
                {new Date(message.created_at).toLocaleTimeString()}
              </div>
            </div>
          </div>
        );

      default: // Regular text message
        return (
          <div key={message.id} style={{
            display: 'flex',
            justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
            marginBottom: '10px'
          }}>
            <div style={{
              background: isOwnMessage 
                ? 'linear-gradient(135deg, #00E9FF, #8A2BE2)' 
                : 'rgba(255, 255, 255, 0.15)',
              color: 'white',
              borderRadius: '18px',
              padding: '12px 18px',
              maxWidth: '70%',
              wordWrap: 'break-word'
            }}>
              <div>{message.content}</div>
              <div style={{ fontSize: '0.7rem', opacity: 0.7, marginTop: '4px' }}>
                {new Date(message.created_at).toLocaleTimeString()}
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div style={{
      height: '100vh',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      display: 'flex',
      flexDirection: 'column',
      color: 'white',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
    }}>
      {/* Header */}
      <div style={{
        padding: '15px 20px',
        background: 'rgba(0, 0, 0, 0.3)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button 
            onClick={onBack}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              fontSize: '1.2rem',
              cursor: 'pointer',
              padding: '8px'
            }}
          >
            ←
          </button>
          {/* Profile Photo */}
          <div style={{ 
            position: 'relative', 
            marginRight: '12px',
            flexShrink: 0
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: matchedUser.photo_url 
                ? `url(${matchedUser.photo_url})` 
                : 'linear-gradient(135deg, #00E9FF, #8A2BE2)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              fontWeight: '600',
              color: 'white',
              border: '2px solid rgba(255, 255, 255, 0.1)'
            }}>
              {!matchedUser.photo_url && (matchedUser.name || 'U').charAt(0).toUpperCase()}
            </div>
          </div>
          
          <div>
            <h3 style={{ margin: 0, fontSize: '1.1rem' }}>
              {matchedUser.name || 'Unknown User'}
            </h3>
            {!userPremiumStatus && (
              <div style={{ fontSize: '0.7rem', color: '#FFD700' }}>
                Upgrade to Premium for gifts & sending and receiving photos
              </div>
            )}
          </div>
        </div>
        
        {userPremiumStatus && (
          <div style={{ color: '#FFD700', fontSize: '0.8rem', fontWeight: '600' }}>
            💎 Premium Active
          </div>
        )}
      </div>

      {/* Messages */}
      <div 
        ref={messagesContainerRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {messages.map(renderMessage)}
        <div ref={messagesEndRef} />
      </div>

      {/* Enhanced Input Area */}
      <div style={{
        padding: '15px 20px',
        background: 'rgba(0, 0, 0, 0.3)',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        {/* Premium Features Row */}
        {userPremiumStatus && (
          <div style={{
            display: 'flex',
            gap: '10px',
            marginBottom: '10px',
            justifyContent: 'center'
          }}>
            <button
              onClick={() => setShowGifts(!showGifts)}
              style={{
                background: showGifts ? 'linear-gradient(45deg, #FF6B6B, #8A2BE2)' : 'rgba(255, 255, 255, 0.1)',
                border: 'none',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '20px',
                fontSize: '0.9rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              🎁 Send Gift
            </button>
            
            <button
              onClick={() => fileInputRef.current?.click()}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: 'none',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '20px',
                fontSize: '0.9rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              disabled={uploading}
            >
              📸 {uploading ? 'Uploading...' : `Send Photo (1 coin) • ${userCoins} available`}
            </button>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              style={{ display: 'none' }}
            />
          </div>
        )}

        {/* Gift Selection Modal */}
        {showGifts && userPremiumStatus && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}>
            <div style={{
              position: 'relative',
              background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
              borderRadius: '20px',
              maxWidth: '90vw',
              maxHeight: '80vh',
              overflow: 'auto',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)'
            }}>
              {/* Close Button */}
              <button
                onClick={() => setShowGifts(false)}
                style={{
                  position: 'absolute',
                  top: '15px',
                  right: '15px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: 'none',
                  color: 'white',
                  width: '30px',
                  height: '30px',
                  borderRadius: '50%',
                  fontSize: '16px',
                  cursor: 'pointer',
                  zIndex: 1001,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                }}
              >
                ×
              </button>
              <VirtualGifts onSendGift={sendGift} />
            </div>
          </div>
        )}

        {/* Text Input */}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
            placeholder="Type your message..."
            style={{
              flex: 1,
              padding: '12px 16px',
              borderRadius: '25px',
              border: 'none',
              background: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              fontSize: '1rem',
              outline: 'none'
            }}
          />
          <button
            onClick={sendMessage}
            style={{
              background: 'linear-gradient(135deg, #00E9FF, #8A2BE2)',
              border: 'none',
              color: 'white',
              padding: '12px 20px',
              borderRadius: '25px',
              fontSize: '1rem',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnhancedChat;